import React, { useState } from 'react';
import { CreditCardIcon, FileTextIcon, TrendingUpIcon, AlertCircleIcon, ClipboardCheckIcon, PlusIcon, ClockIcon, CheckCircleIcon, XCircleIcon, DollarSignIcon, ChevronRightIcon, FilterIcon, UserIcon, CalendarIcon, SearchIcon } from 'lucide-react';
export function ExpenseDashboard({
  locale = 'en-US'
}) {
  // Format currency based on locale
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Format date based on locale
  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Dashboard time filter state
  const [timeFilter, setTimeFilter] = useState('30days');
  const [searchTerm, setSearchTerm] = useState('');
  // Sample data for dashboard
  const expenseStats = {
    totalExpenses: 24750.5,
    pendingApproval: 6499.75,
    reimbursed: 18250.75,
    rejected: 1250.5,
    myDrafts: 3,
    myPending: 2,
    myRejected: 1
  };
  // Sample data for recent expense claims
  const recentExpenses = [{
    id: 'EXP-2023-001',
    title: 'Client Meeting - ABC Corp',
    date: '2023-11-10',
    submittedDate: '2023-11-11',
    amount: 245.75,
    status: 'Pending Approval',
    category: 'Travel',
    submittedBy: 'John Doe'
  }, {
    id: 'EXP-2023-002',
    title: 'Office Supplies',
    date: '2023-11-08',
    submittedDate: '2023-11-09',
    amount: 89.99,
    status: 'Approved',
    category: 'Office',
    submittedBy: 'Jane Smith'
  }, {
    id: 'EXP-2023-003',
    title: 'Conference Registration',
    date: '2023-11-05',
    submittedDate: '2023-11-06',
    amount: 599.0,
    status: 'Reimbursed',
    category: 'Training',
    submittedBy: 'Robert Brown'
  }];
  // Sample data for expenses needing your approval
  const pendingApprovalExpenses = [{
    id: 'EXP-2023-007',
    title: 'Customer Workshop',
    date: '2023-11-15',
    submittedDate: '2023-11-16',
    amount: 1280.5,
    category: 'Events',
    submittedBy: 'Sarah Williams',
    department: 'Marketing'
  }, {
    id: 'EXP-2023-008',
    title: 'Trade Show Attendance',
    date: '2023-11-08',
    submittedDate: '2023-11-12',
    amount: 2450.75,
    category: 'Marketing',
    submittedBy: 'Robert Brown',
    department: 'Marketing'
  }];
  // Get status badge styling
  const getStatusBadgeStyle = status => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Reimbursed':
        return 'bg-blue-100 text-blue-800';
      case 'Pending Approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'Draft':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'Approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-6236" />;
      case 'Reimbursed':
        return <DollarSignIcon className="h-5 w-5 text-blue-500" data-id="element-6237" />;
      case 'Pending Approval':
        return <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-6238" />;
      case 'Rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-6239" />;
      case 'Draft':
      default:
        return <FileTextIcon className="h-5 w-5 text-gray-500" data-id="element-6240" />;
    }
  };
  return <div data-id="element-6241">
      <div className="mb-8 flex justify-between items-center" data-id="element-6242">
        <div data-id="element-6243">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-6244">
            Expense Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6245">
            Overview of expense claims, approvals, and reimbursements
          </p>
        </div>
        <div className="flex items-center space-x-3" data-id="element-6246">
          <div className="flex items-center" data-id="element-6247">
            <FilterIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-6248" />
            <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} className="block text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 p-2 border" data-id="element-6249">
              <option value="7days" data-id="element-6250">Last 7 days</option>
              <option value="30days" data-id="element-6251">Last 30 days</option>
              <option value="90days" data-id="element-6252">Last 90 days</option>
              <option value="ytd" data-id="element-6253">Year to date</option>
            </select>
          </div>
          <button onClick={() => window.setCurrentView && window.setCurrentView('add-expense-claim')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6254">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-6255" />
            New Expense
          </button>
        </div>
      </div>

      {/* Summary Cards Section */}
      <div className="mb-8" data-id="element-6256">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-6257">
          Expense Overview
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-6258">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-6259">
            <div className="p-5" data-id="element-6260">
              <div className="flex items-center" data-id="element-6261">
                <div className="flex-shrink-0" data-id="element-6262">
                  <CreditCardIcon className="h-6 w-6 text-gray-400" data-id="element-6263" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-6264">
                  <dl data-id="element-6265">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6266">
                      Total Expenses
                    </dt>
                    <dd data-id="element-6267">
                      <div className="text-lg font-medium text-gray-900" data-id="element-6268">
                        {formatCurrency(expenseStats.totalExpenses)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-approval')} data-id="element-6269">
            <div className="p-5" data-id="element-6270">
              <div className="flex items-center" data-id="element-6271">
                <div className="flex-shrink-0" data-id="element-6272">
                  <ClockIcon className="h-6 w-6 text-yellow-400" data-id="element-6273" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-6274">
                  <dl data-id="element-6275">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6276">
                      Pending Approval
                    </dt>
                    <dd data-id="element-6277">
                      <div className="text-lg font-medium text-gray-900" data-id="element-6278">
                        {formatCurrency(expenseStats.pendingApproval)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-6279">
            <div className="p-5" data-id="element-6280">
              <div className="flex items-center" data-id="element-6281">
                <div className="flex-shrink-0" data-id="element-6282">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" data-id="element-6283" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-6284">
                  <dl data-id="element-6285">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6286">
                      Reimbursed
                    </dt>
                    <dd data-id="element-6287">
                      <div className="text-lg font-medium text-gray-900" data-id="element-6288">
                        {formatCurrency(expenseStats.reimbursed)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-6289">
            <div className="p-5" data-id="element-6290">
              <div className="flex items-center" data-id="element-6291">
                <div className="flex-shrink-0" data-id="element-6292">
                  <XCircleIcon className="h-6 w-6 text-red-400" data-id="element-6293" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-6294">
                  <dl data-id="element-6295">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6296">
                      Rejected
                    </dt>
                    <dd data-id="element-6297">
                      <div className="text-lg font-medium text-gray-900" data-id="element-6298">
                        {formatCurrency(expenseStats.rejected)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* My Expenses Summary */}
      <div className="mb-8" data-id="element-6299">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-6300">My Expenses</h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3" data-id="element-6301">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-6302">
            <div className="p-5" data-id="element-6303">
              <div className="flex items-center" data-id="element-6304">
                <div className="flex-shrink-0" data-id="element-6305">
                  <FileTextIcon className="h-6 w-6 text-gray-400" data-id="element-6306" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-6307">
                  <dl data-id="element-6308">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6309">
                      My Drafts
                    </dt>
                    <dd data-id="element-6310">
                      <div className="text-lg font-medium text-gray-900" data-id="element-6311">
                        {expenseStats.myDrafts}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-6312">
            <div className="p-5" data-id="element-6313">
              <div className="flex items-center" data-id="element-6314">
                <div className="flex-shrink-0" data-id="element-6315">
                  <ClockIcon className="h-6 w-6 text-yellow-400" data-id="element-6316" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-6317">
                  <dl data-id="element-6318">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6319">
                      My Pending Claims
                    </dt>
                    <dd data-id="element-6320">
                      <div className="text-lg font-medium text-gray-900" data-id="element-6321">
                        {expenseStats.myPending}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-6322">
            <div className="p-5" data-id="element-6323">
              <div className="flex items-center" data-id="element-6324">
                <div className="flex-shrink-0" data-id="element-6325">
                  <XCircleIcon className="h-6 w-6 text-red-400" data-id="element-6326" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-6327">
                  <dl data-id="element-6328">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6329">
                      My Rejected Claims
                    </dt>
                    <dd data-id="element-6330">
                      <div className="text-lg font-medium text-gray-900" data-id="element-6331">
                        {expenseStats.myRejected}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Expenses and Pending Approvals */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-6332">
        {/* Recent Expenses */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-6333">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-6334">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6335">
              Recent Expense Claims
            </h3>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-6336">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-6337">
            {recentExpenses.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-6338">
                No recent expense claims.
              </li> : recentExpenses.map(expense => <li key={expense.id} className="block hover:bg-gray-50 cursor-pointer" onClick={() => window.setCurrentView && window.setCurrentView('expense-claim-detail')} data-id="element-6339">
                  <div className="px-4 py-4 sm:px-6" data-id="element-6340">
                    <div className="flex items-center justify-between" data-id="element-6341">
                      <div className="flex items-center space-x-3" data-id="element-6342">
                        <div className="flex-shrink-0" data-id="element-6343">
                          {getStatusIcon(expense.status)}
                        </div>
                        <div data-id="element-6344">
                          <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-6345">
                            {expense.title}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-6346">
                            {expense.id} • Submitted{' '}
                            {formatDate(expense.submittedDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4" data-id="element-6347">
                        <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeStyle(expense.status)}`} data-id="element-6348">
                          {expense.status}
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-6349">
                          {formatCurrency(expense.amount)}
                        </p>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-6350" />
                      </div>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
        {/* Expenses Needing Your Approval */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-6351">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-6352">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6353">
              Needing Your Approval
            </h3>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-approval')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-6354">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-6355">
            {pendingApprovalExpenses.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-6356">
                No expenses pending your approval.
              </li> : pendingApprovalExpenses.map(expense => <li key={expense.id} className="block hover:bg-gray-50 cursor-pointer" onClick={() => window.setCurrentView && window.setCurrentView('expense-approval')} data-id="element-6357">
                  <div className="px-4 py-4 sm:px-6" data-id="element-6358">
                    <div className="flex items-center justify-between" data-id="element-6359">
                      <div className="flex items-center space-x-3" data-id="element-6360">
                        <div className="flex-shrink-0" data-id="element-6361">
                          <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-6362" />
                        </div>
                        <div data-id="element-6363">
                          <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-6364">
                            {expense.title}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-6365">
                            {expense.id} • Submitted{' '}
                            {formatDate(expense.submittedDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4" data-id="element-6366">
                        <p className="text-sm font-medium text-gray-900" data-id="element-6367">
                          {formatCurrency(expense.amount)}
                        </p>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-6368" />
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between" data-id="element-6369">
                      <div className="sm:flex" data-id="element-6370">
                        <p className="flex items-center text-sm text-gray-500" data-id="element-6371">
                          <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-6372" />
                          {expense.submittedBy} • {expense.department}
                        </p>
                      </div>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>

      {/* Expense Category Distribution */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8" data-id="element-6373">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-6374">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6375">
            Expense Category Distribution
          </h3>
        </div>
        <div className="p-4" data-id="element-6376">
          <div className="h-64 flex items-center justify-center" data-id="element-6377">
            <div className="w-full max-w-3xl" data-id="element-6378">
              {/* Simple bar chart for expense categories */}
              <div className="space-y-4" data-id="element-6379">
                <div data-id="element-6380">
                  <div className="flex items-center justify-between text-sm" data-id="element-6381">
                    <span data-id="element-6382">Travel</span>
                    <span className="font-medium" data-id="element-6383">
                      {formatCurrency(9850.25)} (39.8%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-6384">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-6385">
                      <div style={{
                      width: '39.8%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500" data-id="element-6386"></div>
                    </div>
                  </div>
                </div>
                <div data-id="element-6387">
                  <div className="flex items-center justify-between text-sm" data-id="element-6388">
                    <span data-id="element-6389">Training</span>
                    <span className="font-medium" data-id="element-6390">
                      {formatCurrency(4750.0)} (19.2%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-6391">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-6392">
                      <div style={{
                      width: '19.2%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500" data-id="element-6393"></div>
                    </div>
                  </div>
                </div>
                <div data-id="element-6394">
                  <div className="flex items-center justify-between text-sm" data-id="element-6395">
                    <span data-id="element-6396">Meals</span>
                    <span className="font-medium" data-id="element-6397">
                      {formatCurrency(3250.5)} (13.1%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-6398">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-6399">
                      <div style={{
                      width: '13.1%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500" data-id="element-6400"></div>
                    </div>
                  </div>
                </div>
                <div data-id="element-6401">
                  <div className="flex items-center justify-between text-sm" data-id="element-6402">
                    <span data-id="element-6403">Marketing</span>
                    <span className="font-medium" data-id="element-6404">
                      {formatCurrency(2450.75)} (9.9%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-6405">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-6406">
                      <div style={{
                      width: '9.9%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500" data-id="element-6407"></div>
                    </div>
                  </div>
                </div>
                <div data-id="element-6408">
                  <div className="flex items-center justify-between text-sm" data-id="element-6409">
                    <span data-id="element-6410">Software</span>
                    <span className="font-medium" data-id="element-6411">
                      {formatCurrency(2150.0)} (8.7%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-6412">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-6413">
                      <div style={{
                      width: '8.7%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-yellow-500" data-id="element-6414"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8" data-id="element-6415">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-6416">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6417">
            Quick Actions
          </h3>
        </div>
        <div className="p-4" data-id="element-6418">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-6419">
            <button onClick={() => window.setCurrentView && window.setCurrentView('add-expense-claim')} className="bg-white p-4 border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-200" data-id="element-6420">
              <div className="flex flex-col items-center" data-id="element-6421">
                <div className="bg-indigo-100 p-3 rounded-full mb-3" data-id="element-6422">
                  <PlusIcon className="h-6 w-6 text-indigo-600" data-id="element-6423" />
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-6424">
                  New Expense Claim
                </span>
              </div>
            </button>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-approval')} className="bg-white p-4 border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-200" data-id="element-6425">
              <div className="flex flex-col items-center" data-id="element-6426">
                <div className="bg-yellow-100 p-3 rounded-full mb-3" data-id="element-6427">
                  <ClipboardCheckIcon className="h-6 w-6 text-yellow-600" data-id="element-6428" />
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-6429">
                  Review Approvals
                </span>
              </div>
            </button>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} className="bg-white p-4 border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-200" data-id="element-6430">
              <div className="flex flex-col items-center" data-id="element-6431">
                <div className="bg-blue-100 p-3 rounded-full mb-3" data-id="element-6432">
                  <FileTextIcon className="h-6 w-6 text-blue-600" data-id="element-6433" />
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-6434">
                  View All Expenses
                </span>
              </div>
            </button>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-reports')} className="bg-white p-4 border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-200" data-id="element-6435">
              <div className="flex flex-col items-center" data-id="element-6436">
                <div className="bg-green-100 p-3 rounded-full mb-3" data-id="element-6437">
                  <TrendingUpIcon className="h-6 w-6 text-green-600" data-id="element-6438" />
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-6439">
                  Run Reports
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>;
}